import { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as R from 'ramda';

import * as inventoryExportActions from '@atom/actions/inventoryExportActions';
import { getSelectedSchemaId } from '@atom/selectors/inventoryExportSelectors';
import { InventoryFullCategoryTreeType } from '@atom/types/category';
import { ExportTemplateDetailType } from '@atom/types/inventoryExport';
import { InventorySchemaItem } from '@atom/types/schema';
import { fullCategoryLeftTreeIcon } from '@atom/utilities/iconUtilities';
import { combineSets, diffSets } from '@atom/utilities/setUtilities';

import CategoryTreeRow from './CategoryTreeRow';

import classes from './inventoryExportDetail.module.css';

interface Props {
  categoryTree: InventoryFullCategoryTreeType;
  rootSchemas: InventorySchemaItem[];
  exportTemplateDetail: ExportTemplateDetailType;
  updateSelectedSchema: (schemaId: string) => void;
}

const CategoryPane = ({
  categoryTree,
  rootSchemas,
  exportTemplateDetail,
  updateSelectedSchema,
}: Props) => {
  const dispatch = useDispatch();
  const [selectedCategoryIds, setCategoryIds] = useState(
    new Set(exportTemplateDetail.categoryIds),
  );

  const [selectedSchemaId, setSchemaId] = useState(() => {
    const schemaId = getSelectedSchemaId(categoryTree, exportTemplateDetail);
    updateSelectedSchema(schemaId);
    return schemaId;
  });

  const addCategoryIds = (categoryIds: string[], schemaId: string) => {
    const updatedCategoryIds = [
      ...combineSets(selectedCategoryIds, new Set(categoryIds)),
    ];

    setCategoryIds(new Set(updatedCategoryIds));
    setSchemaId(schemaId);
    updateSelectedSchema(schemaId);

    dispatch(
      inventoryExportActions.requestExportTemplateUpdate({
        exportTemplateId: exportTemplateDetail.id,
        categoryIds: updatedCategoryIds,
        rootSchemaId: schemaId,
      }),
    );
  };

  const removeCategoryIds = (categoryIds: string[]) => {
    const newSelectedCategoryIds = diffSets(
      selectedCategoryIds,
      new Set(categoryIds),
    );

    const updatedCategoryIds = [...newSelectedCategoryIds];
    setCategoryIds(newSelectedCategoryIds);

    if (R.isEmpty(updatedCategoryIds)) {
      setSchemaId('');
      updateSelectedSchema('');
    }

    dispatch(
      inventoryExportActions.requestExportTemplateUpdate({
        exportTemplateId: exportTemplateDetail.id,
        categoryIds: updatedCategoryIds,
      }),
    );
  };

  const toggleCategoryId = (categoryId: string, schemaId: string) => {
    return selectedCategoryIds.has(categoryId)
      ? removeCategoryIds([categoryId])
      : addCategoryIds([categoryId], schemaId);
  };

  const updateCategoryIds = (categoryIds: string[], schemaId?: string) => {
    // If schemaId is defined, add categoryIds
    return schemaId
      ? addCategoryIds(categoryIds, schemaId)
      : removeCategoryIds(categoryIds);
  };

  const leftIcon = fullCategoryLeftTreeIcon();

  return (
    <div className={classes.sectionPane}>
      <div className={classes.paneHeader}>Select Inventory Folder(s)</div>
      <div className={classes.itemTreeContainer}>
        <div className={classes.row}>
          {leftIcon}
          <div className={classes.categoryRowName}>{categoryTree.name}</div>
        </div>
        {categoryTree.children ? (
          categoryTree.children.map(
            (category: InventoryFullCategoryTreeType) => {
              return (
                <CategoryTreeRow
                  key={category.id}
                  category={category}
                  indentation={1}
                  rootSchemas={rootSchemas}
                  toggleCategoryId={toggleCategoryId}
                  updateCategoryIds={updateCategoryIds}
                  selectedCategoryIds={selectedCategoryIds}
                  selectedSchemaId={selectedSchemaId}
                />
              );
            },
          )
        ) : (
          <div />
        )}
      </div>
    </div>
  );
};

export default CategoryPane;
